import { useSyncExternalStore } from "react";
import { CheckCircle2, CircleX, Info, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { CopyableReference, MissingCapability } from "@/components/ui/copyable-reference";

export type ToastVariant = "success" | "error" | "warning" | "info";
export interface ToastData {
  id: number;
  variant: ToastVariant;
  title: string;
  description?: string;
  /** Server-issued support reference (D636). Shown with one-click copy in
   * the compact variant; left out when absent. */
  supportReference?: string | null;
  /** Missing capability a 403 named (D636), display-only. Shown as the
   * localized "Missing capability" line above the reference. */
  capability?: string | null;
}

/** A toast that carries a reference stays up longer, so the person has time
 * to reach the copy button. */
const REFERENCE_MIN_DURATION_MS = 10000;

let toasts: ToastData[] = [];
const listeners = new Set<() => void>();
let nextId = 1;

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
function emit() {
  for (const l of listeners) l();
}
function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}
/** Clears every toast; used between unit tests, since the store is a module singleton. */
export function dismissAllToasts() {
  toasts = [];
  emit();
}
function getSnapshot() {
  return toasts;
}
function getServerSnapshot() {
  return toasts;
}

export function toast({
  variant = "info",
  title,
  description,
  duration = 4000,
  supportReference,
  capability,
}: {
  variant?: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
  supportReference?: string | null;
  capability?: string | null;
}) {
  const id = nextId++;
  toasts = [...toasts.slice(-3), { id, variant, title, description, supportReference, capability }];
  emit();
  setTimeout(() => dismiss(id), supportReference || capability ? Math.max(duration, REFERENCE_MIN_DURATION_MS) : duration);
  return id;
}

const ICONS: Record<ToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: CircleX,
  warning: TriangleAlert,
  info: Info,
};
const TONES: Record<ToastVariant, string> = {
  success: "text-success",
  error: "text-danger",
  warning: "text-warning",
  info: "text-info",
};

export function Toaster() {
  const list = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  if (list.length === 0) return null;
  return (
    <div
      aria-live="polite"
      role="status"
      className="pointer-events-none fixed bottom-4 end-4 z-[70] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {list.map((t) => {
        const Icon = ICONS[t.variant];
        return (
          <div
            key={t.id}
            className="pointer-events-auto flex items-start gap-3 rounded-lg border border-line bg-surface p-3.5 shadow-soft"
          >
            <Icon aria-hidden className={cn("mt-0.5 size-4 shrink-0", TONES[t.variant])} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-ink">{t.title}</div>
              {t.description && <p className="mt-0.5 text-xs leading-relaxed text-muted">{t.description}</p>}
              <MissingCapability capability={t.capability} className="mt-1.5 text-xs text-muted" />
              <CopyableReference reference={t.supportReference} compact className="mt-1.5 text-muted" />
            </div>
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
              className="grid size-6 shrink-0 place-items-center rounded text-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
            >
              <CircleX aria-hidden className="size-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}